<template>
  <Sidebar collapsible="icon" variant="inset">
    <SidebarHeader>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" as-child>
            <RouterLink to="/">
              <Avatar
                v-if="isCollapsed"
                class="size-8 rounded-lg bg-linear-to-br from-gradient-agent-start to-gradient-agent-end text-white"
              >
                <AvatarFallback
                  class="rounded-lg bg-linear-to-br from-gradient-agent-start to-gradient-agent-end text-white font-semibold"
                >
                  A
                </AvatarFallback>
              </Avatar>
              <img
                v-else
                :src="isDark ? atlastWhiteLogo : atlastPurpleLogo"
                alt="Atlast Logo"
                class="h-7.5"
              />
            </RouterLink>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarHeader>
    <SidebarContent>
      <NavMain :items="data.navMain" />
    </SidebarContent>
    <SidebarFooter>
      <NavUser :user="data.user" />
    </SidebarFooter>
    <SidebarRail />
  </Sidebar>
</template>

<script setup lang="ts">
import NavMain from '@/components/NavMain.vue'
import NavUser from '@/components/NavUser.vue'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar'
import { useBuildInfo } from '@/hooks/useBuildInfo'
import { i18n } from '@/plugins/i18n'
import { useAuthStore } from '@/stores/auth'
import atlastPurpleLogo from '@/assets/images/atlast-purple.svg'
import atlastWhiteLogo from '@/assets/images/atlast-white.svg'
import { useDark } from '@vueuse/core'
import {
  Building,
  CodeXml,
  CreditCard,
  FileChartColumn,
  KeyRound,
  LayoutDashboard,
  Mail,
  Package,
  ShieldCheck,
  SquareCode,
  Users,
} from 'lucide-vue-next'
import { computed } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()
const authStore = useAuthStore()

const { isProductionBuild } = useBuildInfo()

const isDark = useDark()
const { state } = useSidebar()
const isCollapsed = computed(() => state.value === 'collapsed')

const user = computed(() => authStore.loggedInUser)

// Permission mapping for sidebar items
const permissionMap: Record<string, string> = {
  '/organizations': 'admin_orgs_read',
  '/organizations/groups': 'admin_org_access_read',
  '/plans': 'admin_plan_catalog_read',
  '/billing/credit-pricing': 'admin_billing_read',
  '/onboarding/email-templates': 'admin_orgs_read',
  '/users': 'admin_users_read',
  '/modules': 'admin_features_read',
  '/admin-users': 'admin_admin_users_read',
  '/permission-templates': 'admin_permission_template_read',
  '/llm-usage': 'admin_ai_read',
  '/feedback': 'admin_feedback_read',
  '/redis': 'admin_redis_read',
  '/maintenance': 'admin_system_update',
}

// Check if a route is visible based on permissions
const canAccessRoute = (url: string): boolean => {
  const requiredPermission = permissionMap[url]
  // No permission required for this route
  if (!requiredPermission) return true
  return authStore.hasPermission(requiredPermission)
}

const data = computed(() => {
  const allNavItems = [
    {
      title: i18n.global.t('modules.dashboard.title'),
      url: '/dashboard',
      icon: LayoutDashboard,
      isActive: route.path.startsWith('/dashboard'),
    },
    {
      title: i18n.global.t('modules.organizations.title', 2),
      url: '/organizations',
      icon: Building,
      isActive: route.path.startsWith('/organizations'),
      items: [
        {
          title: i18n.global.t('modules.groups.title', 2),
          url: '/organizations/groups',
        },
      ],
    },
    {
      title: i18n.global.t('modules.plans.title', 2),
      url: '/plans',
      icon: Package,
      isActive: route.path.startsWith('/plans'),
    },
    {
      title: i18n.global.t('modules.billing.title'),
      url: '',
      icon: CreditCard,
      isActive: route.path.startsWith('/billing'),
      items: [
        {
          title: i18n.global.t('modules.billing.creditPricing.navTitle'),
          url: '/billing/credit-pricing',
        },
      ],
    },
    {
      title: i18n.global.t('modules.onboarding.title'),
      url: '/onboarding/email-templates',
      icon: Mail,
      isActive: route.path.startsWith('/onboarding'),
    },
    {
      title: i18n.global.t('modules.users.title', 2),
      url: '/users',
      icon: Users,
      isActive: route.path.startsWith('/users'),
    },
    {
      title: i18n.global.t('modules.module.title', 2),
      url: '/modules',
      icon: SquareCode,
      isActive: route.path.startsWith('/modules'),
    },
    {
      title: i18n.global.t('modules.admin_users.title', 2),
      url: '/admin-users',
      icon: ShieldCheck,
      isActive: route.path.startsWith('/admin-users'),
    },
    {
      title: i18n.global.t('modules.permission_templates.title', 2),
      url: '/permission-templates',
      icon: KeyRound,
      isActive: route.path.startsWith('/permission-templates'),
    },
    {
      title: i18n.global.t('modules.technical.title'),
      url: '',
      icon: FileChartColumn,
      items: [
        {
          title: i18n.global.t('modules.llm_usage.title'),
          url: '/llm-usage',
        },
        {
          title: i18n.global.t('modules.feedback.title'),
          url: '/feedback',
        },
        {
          title: i18n.global.t('modules.redis.title'),
          url: '/redis',
        },
        {
          title: i18n.global.t('modules.maintenance.title'),
          url: '/maintenance',
        },
      ],
    },
    {
      title: i18n.global.t('modules.dev.title'),
      url: '/dev',
      icon: CodeXml,
    },
  ]

  // Filter nav items based on permissions
  const filteredNavItems = allNavItems
    .filter((item) => {
      // Filter out dev in production
      if (item.url === '/dev' && isProductionBuild.value) return false
      // Check permission for items with a URL
      if (item.url) return canAccessRoute(item.url)
      // For items without URL (parent groups), keep them for now
      return true
    })
    .map((item) => {
      // Filter nested items based on permissions
      if (item.items) {
        const filteredItems = item.items.filter((subItem) => canAccessRoute(subItem.url))
        return { ...item, items: filteredItems }
      }
      return item
    })
    .filter((item) => {
      // Remove parent groups with no visible children (items without URL that have empty items array)
      if (!item.url && item.items && item.items.length === 0) return false
      return true
    })

  return {
    user: {
      name: user.value?.name || '',
      email: user.value?.email || '',
      avatar: user.value?.picture || '',
    },
    navMain: filteredNavItems,
  }
})
</script>
